import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import ActionButton from "@/components/Button/ActionButton";
import { AttributeCard } from "@/components/Card/AttributeCard";
import Icon from "@/components/Icon/Icon";
import type { IconName } from "@/components/Icon/iconMap";
import { Grid, Spacer, VStack } from "@/components/Layout";
import StepperHeaderComponent from "@/components/StepperHeader";
import { Text } from "@/components/Typography/Text";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { defaultNS, useTranslation } from "@/lib/i18n";

import { BusinessPartnerType } from "../_list/-constants";

import { AddBusinessPartnerStepKeys } from "./-constants/constant";

export const Route = createFileRoute("/_staff/business-partner/add/")({
  component: Introduce,
});

type CommonProps = {
  selectedBusinessPartnerType: BusinessPartnerType | null;
  setSelectedBusinessPartnerType: (type: BusinessPartnerType) => void;
};

function Introduce() {
  const [selectedBusinessPartnerType, setSelectedBusinessPartnerType] =
    useState<BusinessPartnerType>(BusinessPartnerType.CORPORATION);

  return (
    <VStack className="mx-auto w-[1000px]">
      <Header />
      <Spacer variant="xs" />
      <BusinessPartnerTypeRadioGroup
        selectedBusinessPartnerType={selectedBusinessPartnerType}
        setSelectedBusinessPartnerType={setSelectedBusinessPartnerType}
      />
      <Spacer variant="xxl" />
    </VStack>
  );
}

function Header() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.businessPartner.add.intro.header",
  });

  return (
    <VStack>
      <Spacer variant="xxl" />
      <StepperHeaderComponent headerTitle={t("headerTitle")} />
    </VStack>
  );
}

function BusinessPartnerTypeRadioGroup({
  selectedBusinessPartnerType,
  setSelectedBusinessPartnerType,
}: CommonProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.businessPartner",
  });

  const BUSINESS_PARTNER_TYPES: {
    id: BusinessPartnerType;
    title: string;
    requiredDetails: { iconName: IconName; text: string }[];
  }[] = [
    {
      id: BusinessPartnerType.CORPORATION,
      title: t("type.corporation"),
      requiredDetails: [
        {
          iconName: "SquareDot",
          text: t(
            "add.form.fields.basicIdInformation.businessPartnerName.label",
          ),
        },
        {
          iconName: "SquareDot",
          text: t(
            "add.form.fields.basicIdInformation.businessPartnerRoles.label",
          ),
        },
      ],
    },
    {
      id: BusinessPartnerType.INDIVIDUAL,
      title: t("type.individual"),
      requiredDetails: [
        {
          iconName: "SquareDot",
          text: t(
            "add.form.fields.basicIdInformation.businessPartnerName.label",
          ),
        },
        {
          iconName: "SquareDot",
          text: t(
            "add.form.fields.basicIdInformation.businessPartnerRoles.label",
          ),
        },
        {
          iconName: "SquareDot",
          text: t("add.form.fields.individualIdInformation.personId.label"),
        },
      ],
    },
    // TODO: GROUPの定義が確定してから対応
    // {
    //   id: BusinessPartnerType.GROUP,
    //   title: t("type.group"),
    //   requiredDetails: [
    //     {
    //       iconName: "SquareDot",
    //       text: t(
    //         "add.form.fields.basicIdInformation.businessPartnerName.label",
    //       ),
    //     },
    //     {
    //       iconName: "SquareDot",
    //       text: t(
    //         "add.form.fields.basicIdInformation.businessPartnerRoles.label",
    //       ),
    //     },
    //   ],
    // },
  ];

  const navigate = useNavigate();

  const handleRadioChange = useCallback(
    (value: BusinessPartnerType) => {
      setSelectedBusinessPartnerType(value);
    },
    [setSelectedBusinessPartnerType],
  );

  const handleButtonClick = useCallback(() => {
    if (selectedBusinessPartnerType) {
      navigate({
        to: "/business-partner/add/$businessPartnerType",
        params: { businessPartnerType: selectedBusinessPartnerType },
        hash: AddBusinessPartnerStepKeys.BASIC_ID_INFORMATION,
      });
    }
  }, [selectedBusinessPartnerType, navigate]);

  return (
    <>
      <Spacer variant="xl" />
      <Text variant="h4">{t("add.intro.body.selectBusinessPartnerType")}</Text>
      <Spacer variant="l" />
      <RadioGroup onValueChange={handleRadioChange}>
        <Grid columns={2} rowGap={28} columnGap={24}>
          {BUSINESS_PARTNER_TYPES.map((type) => (
            <AttributeCard
              key={type.id}
              title={type.title}
              titleStyle="CardHeader"
              onClick={() => handleRadioChange(type.id)}
              actionButton={
                <RadioGroupItem
                  id={type.id}
                  value={type.id}
                  className="h-4 w-4"
                  checked={selectedBusinessPartnerType === type.id}
                />
              }
              actionButtonPosition="top-left"
              actionButtonIsInner={false}
              className="cursor-pointer"
            >
              <VStack className="gap-3">
                <Text variant="labelSmall">
                  {t("add.intro.body.itemCard.requiredDetails")}
                </Text>
                {type.requiredDetails.map((detail, index) => (
                  <Icon
                    key={`${detail.text}-${index}`}
                    iconName={detail.iconName}
                    text={detail.text}
                  />
                ))}
              </VStack>
            </AttributeCard>
          ))}
        </Grid>
      </RadioGroup>
      <Spacer variant="xl" />
      <ActionButton
        icon="FilePenIcon"
        className="w-48"
        onClick={handleButtonClick}
      >
        {t("add.intro.body.fillOutDetails")}
      </ActionButton>
    </>
  );
}
